import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { invokeFunction } from '../lib/supabase-functions'
import { useAuth } from './AuthContext'
import { useOrganization } from './OrganizationContext'
import { detectHelpQuery, searchHelp, buildHelpContext, buildHelpEnhancedMessage, buildHelpFooter } from '../lib/helpSearch'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  metadata?: Record<string, unknown>
}

interface AIContextType {
  messages: Message[]
  isLoading: boolean
  error: string | null
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>
  clearMessages: () => void
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

interface SendMessageOptions {
  tools?: string[]
  systemPrompt?: string
  onStream?: (chunk: string) => void
}

const AIContext = createContext<AIContextType | undefined>(undefined)

export function AIProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { organization } = useOrganization()
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const queryClient = useQueryClient()

  const sendMessage = useCallback(async (content: string, options?: SendMessageOptions) => {
    if (!user || !organization) {
      setError('Please log in to use the AI assistant')
      return
    }

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()

    // Add user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setError(null)

    try {
      // Call Supabase Edge Function with proper auth
      console.log('Calling AI chat with:', {
        organizationId: organization.id,
        userId: user.id,
        messageCount: messages.length + 1
      })

      // Check if this is a help-related query
      const isHelpQuery = detectHelpQuery(content)
      let helpSystemPrompt = options?.systemPrompt
      let helpArticles: Awaited<ReturnType<typeof searchHelp>> = []

      if (isHelpQuery) {
        console.log('Detected help query, fetching relevant articles...')
        try {
          helpArticles = await searchHelp(content, 3)
          if (helpArticles.length > 0) {
            const helpContext = buildHelpContext(helpArticles)
            const enhancedMessage = buildHelpEnhancedMessage(content, helpContext)
            helpSystemPrompt = enhancedMessage
            console.log('Injected help context from', helpArticles.length, 'articles')
          }
        } catch (helpError) {
          console.warn('Failed to fetch help articles:', helpError)
          // Continue without help context
        }
      }

      // Ensure all values are properly defined and are strings
      const requestBody: Record<string, unknown> = {
        messages: [...messages, userMessage],
        organizationId: organization.id,
        userId: user.id,
        tools: options?.tools || ['member_lookup', 'schedule_query', 'revenue_report', 'booking_search', 'payment_schedules_report', 'booking_actions', 'facility_actions', 'class_actions', 'client_actions', 'camp_actions', 'invoice_actions', 'contract_actions', 'package_actions', 'membership_actions', 'family_actions', 'marketing_actions', 'waitlist_actions', 'staff_actions', 'settings_actions'],
        context: {
          organizationName: organization.name || 'Unknown Organization',
          userRole: (user?.role || 'user').toString(),
          isHelpQuery,
          helpArticleCount: helpArticles.length
        }
      }

      // Add optional fields only if they're defined
      if (helpSystemPrompt) {
        requestBody.systemPrompt = helpSystemPrompt
      } else if (options?.systemPrompt) {
        requestBody.systemPrompt = options.systemPrompt
      }
      
      console.log('Request body:', JSON.stringify(requestBody, null, 2))
      
      const { data, error: apiError } = await invokeFunction('ai-chat', requestBody)

      if (apiError) {
        console.error('Edge Function Error:', apiError)
        // Check if there's a response body with more details
        if (apiError.context?.body) {
          // If body is a ReadableStream, try to read it
          if (apiError.context.body instanceof ReadableStream) {
            try {
              const reader = apiError.context.body.getReader()
              const decoder = new TextDecoder()
              let errorText = ''
              
              while (true) {
                const { done, value } = await reader.read()
                if (done) break
                errorText += decoder.decode(value)
              }
              
              console.error('Error details:', errorText)
              // Try to parse as JSON
              try {
                const errorData = JSON.parse(errorText)
                throw new Error(errorData.error || errorData.message || 'Unknown error from Edge Function')
              } catch {
                throw new Error(errorText || 'Unknown error from Edge Function')
              }
            } catch (streamError) {
              console.error('Failed to read error stream:', streamError)
            }
          } else {
            console.error('Error details:', apiError.context.body)
          }
        }
        throw apiError
      }
      
      if (!data) {
        throw new Error('No response from AI service')
      }

      // Handle streaming response if supported
      if (data.stream && options?.onStream) {
        // Handle streaming
        const reader = data.stream.getReader()
        const decoder = new TextDecoder()
        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          metadata: {
            isHelpResponse: isHelpQuery,
            helpArticles: helpArticles.map(a => ({ id: a.id, title: a.title, slug: a.slug, category: a.category }))
          }
        }

        setMessages(prev => [...prev, assistantMessage])

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value)
          assistantMessage.content += chunk
          options.onStream(chunk)

          // Update message with accumulated content
          setMessages(prev =>
            prev.map(msg => msg.id === assistantMessage.id
              ? { ...msg, content: assistantMessage.content }
              : msg
            )
          )
        }

        // Append help footer after streaming completes
        if (isHelpQuery && helpArticles.length > 0) {
          const helpFooter = buildHelpFooter(helpArticles[0])
          assistantMessage.content += helpFooter
          options.onStream(helpFooter)
          setMessages(prev =>
            prev.map(msg => msg.id === assistantMessage.id
              ? { ...msg, content: assistantMessage.content }
              : msg
            )
          )
        }
      } else {
        // Non-streaming response
        let responseContent = data.content

        // Append help footer if this was a help query with articles
        if (isHelpQuery && helpArticles.length > 0) {
          const helpFooter = buildHelpFooter(helpArticles[0])
          responseContent = responseContent + helpFooter
        }

        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: responseContent,
          timestamp: new Date(),
          metadata: {
            ...data.metadata,
            isHelpResponse: isHelpQuery,
            helpArticles: helpArticles.map(a => ({ id: a.id, title: a.title, slug: a.slug, category: a.category }))
          }
        }
        setMessages(prev => [...prev, assistantMessage])
      }

      // Invalidate common queries so UI reflects any changes the AI made
      queryClient.invalidateQueries({ queryKey: ['facilities'] })
      queryClient.invalidateQueries({ queryKey: ['bookingTypes'] })
      queryClient.invalidateQueries({ queryKey: ['booking-types'] })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.invalidateQueries({ queryKey: ['calendar'] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['camps'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      queryClient.invalidateQueries({ queryKey: ['packages-management'] })
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.log('Request aborted')
      } else {
        console.error('AI Error:', err)
        setError(err instanceof Error ? err.message : 'Failed to get AI response')
      }
    } finally {
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [user, organization, messages])

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  const value: AIContextType = {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    isOpen,
    setIsOpen
  }

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>
}

export function useAI() {
  const context = useContext(AIContext)
  if (context === undefined) {
    throw new Error('useAI must be used within an AIProvider')
  }
  return context
}